import { type ApiViolation, getApiViolations, ResponseError } from '~/api/utils'

export type ApiProblem = {
  type: string
  title?: string
  detail?: string
  status?: number
  instance?: string
  extensions: Record<string, unknown>
  violations: ApiViolation[]
}

const STANDARD_MEMBERS = new Set([
  'type',
  'title',
  'status',
  'detail',
  'instance',
  // legacy mirrors / non machine-readable members kept out of `extensions`
  'message',
  'code',
  'violations',
  'details',
  'traces'
])

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined

/**
 * Single entry point to read an API error as an RFC 7807 Problem Details
 * document. Returns null when the rejection is not an API response error
 * (e.g. a network failure), letting callers fall back to a generic message.
 */
export const getApiError = (error: unknown): ApiProblem | null => {
  if (!(error instanceof ResponseError)) {
    return null
  }

  const data = error.responseData ?? {}
  const extensions: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(data)) {
    if (!STANDARD_MEMBERS.has(key)) {
      extensions[key] = value
    }
  }

  return {
    type: asString(data.type) ?? 'about:blank',
    title: asString(data.title),
    detail: asString(data.detail),
    status: typeof data.status === 'number' ? data.status : error.httpStatus,
    instance: asString(data.instance),
    extensions,
    violations: getApiViolations(error)
  }
}
